import React, {useEffect, useState} from "react";
import {Layout, Button, Switch, Space, AutoComplete, Select, message, Alert} from "antd";
import {PlusOutlined, SaveOutlined} from "@ant-design/icons";
import Editor from "@monaco-editor/react";
import {useDispatch, useSelector} from "react-redux";

import AppHeader from "../../AppHeader/AppHeader";
import ManyosSpinner from "../../ManyosSpinner/ManyosSpinner";
import PersonPanel from "../../Anonymizer/PersonPanel";
import TextPanel from "../../Anonymizer/TextPanel";
import DeletePanel from "../../Anonymizer/DeletePanel";
import DiscoveryPanel from "../../Anonymizer/DiscoveryPanel";
import { anonApi } from "../../util/anonymizerApi";
import {
    getAnonymizerConfig,
    saveAnonymizerConfig,
    getAnonymizerForms,
    getAnonymizerConnectors,
    getConnectorForms,
    loadFieldsForForm,
    updateAnonymizerConfig,
    setAnonymizerCodeView,
    normalizeConnector
} from "../../util/redux/anonymizerSlice";

const {Content} = Layout;

// inline control: pick a source connector + form, append a new entry.
// The connector select shows all loaded connectors (see /v1/connectors) -
// custom connectors dropped into the API's connectors/ dir appear here.
const AddEntry = ({forms, formsByConnector, connectors, label, onAdd, onConnectorChange}) => {
    const [form, setForm] = useState("");
    const [connector, setConnector] = useState("remedy");
    const multiSource = (connectors || []).length > 0;
    const formOptions = connector === "remedy"
        ? (forms || [])
        : ((formsByConnector || {})[connector] || []);
    return (
        <Space style={{margin: "12px 0"}}>
            {multiSource && (
                <Select
                    value={connector}
                    onChange={(c) => {
                        setConnector(c);
                        if (onConnectorChange) onConnectorChange(c);
                    }}
                    style={{width: 220}}
                    options={connectors.map((c) => ({value: c.name, label: c.displayName || c.name}))}
                    aria-label="Source system connector"
                />
            )}
            <AutoComplete
                value={form}
                options={formOptions.map((f) => ({value: f}))}
                filterOption={(input, o) => o.value.toLowerCase().includes(input.toLowerCase())}
                onChange={setForm}
                style={{width: 320}}
                placeholder={connector === "remedy" ? "Form, e.g. HPD:Help Desk" : "Record definition / collection"}
            />
            <Button
                icon={<PlusOutlined/>}
                type="dashed"
                disabled={!form}
                onClick={() => {
                    onAdd(form, connector !== "remedy" ? connector : undefined);
                    setForm("");
                }}
            >
                {label}
            </Button>
        </Space>
    );
};

// step subtitles for the stepper (purely presentational)
const STEP_SUB = {
    discovery: "where is it?",
    persons: "fake identity",
    text: "filler",
    mltext: "mask",
    delete: "records"
};

const Anonymizer = () => {
    const dispatch = useDispatch();
    const [profiles, setProfiles] = useState([]);
    // remember the open tab so a page refresh doesn't jump back to the first one
    const [activeTab, setActiveTabState] = useState(() => localStorage.getItem("sc.tab.anonymizer") || "discovery");
    const setActiveTab = (k) => {
        setActiveTabState(k);
        try { localStorage.setItem("sc.tab.anonymizer", k); } catch (e) { /* storage unavailable */ }
    };
    const {config, fields, forms, formsByConnector, connectors, loading, unsaved, codeView, error} = useSelector((state) => state.anonymizer);

    const prevUnsavedRef = React.useRef(false);
    useEffect(() => {
        if (prevUnsavedRef.current && !unsaved && !error) {
            message.success("Saved successfully!");
        }
        prevUnsavedRef.current = unsaved;
    }, [unsaved, error]);

    useEffect(() => {
        if (!config || config.length === 0) {
            dispatch(getAnonymizerConfig());
        }
        if (!forms || forms.length === 0) {
            dispatch(getAnonymizerForms());
        }
        dispatch(getAnonymizerConnectors());
        anonApi(`/v1/profiles`, {method: "GET"})
            .then((d) => setProfiles((d && d.profiles) || []))
            .catch(() => setProfiles([]));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const formFieldsFor = (form, connector) => {
        const found = fields.find((e) =>
            e.form === form && normalizeConnector(e.connector) === normalizeConnector(connector));
        return found ? found.fields : undefined;
    };

    const ensureFields = (form, connector) => {
        if (form && !fields.find((e) =>
            e.form === form && normalizeConnector(e.connector) === normalizeConnector(connector))) {
            dispatch(loadFieldsForForm({form, connector}));
        }
    };

    // lazy-load the collection list of a connector for the form autocomplete
    const ensureConnectorForms = (connector) => {
        if (connector && connector !== "remedy" && !(formsByConnector || {})[connector]) {
            dispatch(getConnectorForms(connector));
        }
    };

    const setBlock = (key, value) => {
        let replaced = false;
        const newConfig = (config || []).map((b) => {
            if (b && b[key] !== undefined) {
                replaced = true;
                return {[key]: value};
            }
            return b;
        });
        if (!replaced) {
            newConfig.push({[key]: value});
        }
        dispatch(updateAnonymizerConfig({config: newConfig}));
    };

    const personConfig = ((config || []).find((b) => b && b.personConfig) || {}).personConfig || [];
    const textConfig = ((config || []).find((b) => b && b.textConfig) || {}).textConfig || [];
    const deleteConfig = ((config || []).find((b) => b && b.deleteConfig) || {}).deleteConfig || [];
    const mlEntries = (((config || []).find((b) => b && b.anonymizeText) || {}).anonymizeText || {}).entries || [];

    const replaceAt = (list, i, value) => list.map((e, idx) => (idx === i ? value : e));
    const removeAt = (list, i) => list.filter((_, idx) => idx !== i);

    const tabItems = [
        {
            label: "Discovery",
            key: "discovery",
            children: <DiscoveryPanel/>
        },
        {
            label: "Persons",
            key: "persons",
            children: (
                <>
                    {personConfig.map((c, i) => (
                        <PersonPanel
                            key={"person-" + i}
                            entry={c}
                            formFields={formFieldsFor(c.form, c.connector)}
                            allFields={fields}
                            forms={forms}
                            formsByConnector={formsByConnector}
                            connectors={connectors}
                            onNeedFields={ensureFields}
                            onNeedConnectorForms={ensureConnectorForms}
                            onChange={(entry) => setBlock("personConfig", replaceAt(personConfig, i, entry))}
                            onDelete={() => setBlock("personConfig", removeAt(personConfig, i))}
                        />
                    ))}
                    <AddEntry
                        forms={forms}
                        formsByConnector={formsByConnector}
                        onConnectorChange={ensureConnectorForms}
                        connectors={connectors}
                        label={"add person form"}
                        onAdd={(form, connector) => {
                            ensureFields(form, connector);
                            setBlock("personConfig", [
                                ...personConfig,
                                {form: form, ...(connector ? {connector} : {}), type: "person", query: "", fields: {}, relatedRecords: []}
                            ]);
                        }}
                    />
                </>
            )
        },
        {
            label: "Text (random)",
            key: "text",
            children: (
                <>
                    {textConfig.map((c, i) => (
                        <TextPanel
                            key={"text-" + i}
                            index={"text-" + i}
                            entry={c}
                            formFields={formFieldsFor(c.form, c.connector)}
                            connectors={connectors}
                            onNeedFields={ensureFields}
                            actions={["sentence", "paragraph", "clear", "constant"]}
                            onChange={(entry) => setBlock("textConfig", replaceAt(textConfig, i, entry))}
                            onDelete={() => setBlock("textConfig", removeAt(textConfig, i))}
                        />
                    ))}
                    <AddEntry
                        forms={forms}
                        formsByConnector={formsByConnector}
                        onConnectorChange={ensureConnectorForms}
                        connectors={connectors}
                        label={"add text form"}
                        onAdd={(form, connector) => {
                            ensureFields(form, connector);
                            setBlock("textConfig", [...textConfig, {form: form, ...(connector ? {connector} : {}), type: "text", query: "", fields: {}}]);
                        }}
                    />
                </>
            )
        },
        {
            label: "Remove PII (ML)",
            key: "mltext",
            children: (
                <>
                    {mlEntries.map((c, i) => (
                        <TextPanel
                            key={"mltext-" + i}
                            index={"mltext-" + i}
                            entry={c}
                            profiles={profiles}
                            formFields={formFieldsFor(c.form, c.connector)}
                            connectors={connectors}
                            onNeedFields={ensureFields}
                            actions={["removePII", "constant"]}
                            onChange={(entry) => setBlock("anonymizeText", {entries: replaceAt(mlEntries, i, entry)})}
                            onDelete={() => setBlock("anonymizeText", {entries: removeAt(mlEntries, i)})}
                        />
                    ))}
                    <AddEntry
                        forms={forms}
                        formsByConnector={formsByConnector}
                        onConnectorChange={ensureConnectorForms}
                        connectors={connectors}
                        label={"add PII form"}
                        onAdd={(form, connector) => {
                            ensureFields(form, connector);
                            setBlock("anonymizeText", {entries: [...mlEntries, {form: form, ...(connector ? {connector} : {}), query: "", fields: {}}]});
                        }}
                    />
                </>
            )
        },
        {
            label: "Delete Records",
            key: "delete",
            children: (
                <>
                    {deleteConfig.map((c, i) => (
                        <DeletePanel
                            key={`deletepanel-${i}`}
                            index={i}
                            entry={c}
                            allFields={fields}
                            connectors={connectors}
                            onNeedFields={ensureFields}
                            onChange={(entry) => setBlock("deleteConfig", replaceAt(deleteConfig, i, entry))}
                            onDelete={() => setBlock("deleteConfig", removeAt(deleteConfig, i))}
                        />
                    ))}
                    <AddEntry
                        forms={forms}
                        formsByConnector={formsByConnector}
                        onConnectorChange={ensureConnectorForms}
                        connectors={connectors}
                        label={"add delete rule"}
                        onAdd={(form, connector) => {
                            ensureFields(form, connector);
                            setBlock("deleteConfig", [...deleteConfig, {form: form, ...(connector ? {connector} : {}), query: "", description: ""}]);
                        }}
                    />
                </>
            )
        }
    ];

    return (
        <Layout style={{minHeight: "100vh", backgroundColor: "var(--sc-ground)"}}>
            <AppHeader
                area="anonymizer"
                extra={
                    <Space size="middle">
                        <span style={{fontSize: 13}}>Code View <Switch checked={codeView} onChange={(v) => dispatch(setAnonymizerCodeView(v))}/></span>
                        {unsaved ? (
                            <Button
                                icon={<SaveOutlined/>}
                                type="primary"
                                loading={loading}
                                onClick={() => dispatch(saveAnonymizerConfig())}
                            >
                                Save
                            </Button>
                        ) : (
                            <span style={{fontSize: 12, color: "rgba(255,255,255,0.45)"}}>✓ gespeichert</span>
                        )}
                    </Space>
                }
            />
            <Content style={{padding: "24px"}}>
                <div className="sc-page">
                    {error && <Alert type="error" showIcon message={error} style={{marginBottom: 12}}/>}
                    <ManyosSpinner spinning={loading}>
                        {codeView ? (
                            <Editor
                                theme="vs-dark"
                                defaultValue={JSON.stringify(config, 0, 2)}
                                key={"anonymizer.config"}
                                defaultLanguage={"json"}
                                onChange={(code) => {
                                    try {
                                        dispatch(updateAnonymizerConfig({config: JSON.parse(code)}));
                                    } catch (e) {
                                        // ignore json errors while typing
                                    }
                                }}
                                height={"80vh"}
                            />
                        ) : (
                            <>
                                <div className="sc-stepper" style={{marginBottom: 18}}>
                                    {tabItems.map((it, idx) => (
                                        <button
                                            key={it.key}
                                            type="button"
                                            className={"sc-step" + (activeTab === it.key ? " active" : "")}
                                            onClick={() => setActiveTab(it.key)}
                                        >
                                            <span className="num">{idx + 1}</span>
                                            <span className="t">{it.label}<small>{STEP_SUB[it.key]}</small></span>
                                        </button>
                                    ))}
                                </div>
                                {(tabItems.find((t) => t.key === activeTab) || tabItems[0]).children}
                            </>
                        )}
                    </ManyosSpinner>
                </div>
            </Content>
        </Layout>
    );
};

export default Anonymizer;
